/**
 * Browse Home — public customer-tenants for entity=tenant / Vše Tenanti / listKind=tenant.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { filterDirectoryOrgs } from '../../shared/ui/directoryListFilter.js';
import { orgDirectoryQueryOptions } from '../tenant-select/hooks/useOrgDirectoryRealtimeSync.js';
import {
  fetchPublicCustomerTenants,
  type PublicCustomerTenantDTO,
} from '../tenant-select/publicTenantApi.js';
import { usePublicDirectorySseConnected } from '../shops/hooks/PublicDirectorySseProvider.js';
import { browseNeedsTenantDirectory } from './browseEntityResultsRouting.js';
import type { DiscoverySearchParamsState } from '../discovery/discoverySearchParams.js';

export type UseBrowseTenantDirectoryResult = {
  readonly items: readonly PublicCustomerTenantDTO[];
  readonly allItems: readonly PublicCustomerTenantDTO[];
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly refetch: () => void;
};

export function useBrowseTenantDirectory(
  params: Pick<DiscoverySearchParamsState, 'view' | 'q' | 'entity' | 'listKind'>,
): UseBrowseTenantDirectoryResult {
  const { apiBaseUrl } = useRuntimeConfig();
  const directorySseConnected = usePublicDirectorySseConnected();
  const enabled = browseNeedsTenantDirectory(params);
  const q = params.q.trim();

  const tenantsQuery = useQuery({
    queryKey: customerQueryKeys.publicCustomerTenants(apiBaseUrl),
    queryFn: ({ signal }) => fetchPublicCustomerTenants(apiBaseUrl, signal),
    ...orgDirectoryQueryOptions(directorySseConnected),
    enabled,
  });

  const allItems = useMemo(
    () => (enabled ? (tenantsQuery.data ?? []) : []),
    [enabled, tenantsQuery.data],
  );

  const items = useMemo(
    () =>
      filterDirectoryOrgs(allItems, {
        query: q,
        sort: 'nameAsc',
      }),
    [allItems, q],
  );

  return {
    items,
    allItems,
    isPending: enabled && tenantsQuery.isPending,
    isError: enabled && tenantsQuery.isError,
    refetch: () => {
      void tenantsQuery.refetch();
    },
  };
}
